/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeField,
  DimeFieldContent,
  DimeFieldDescription,
  DimeFieldGroup,
  DimeFieldLabel,
  DimeFieldTitle,
  DimeSeparator,
  DimeSwitch,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/switch')({
  component: SwitchDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-start gap-6 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

const NOTIFICATION_OPTIONS = [
  { id: 'sw-email', label: 'Email notifications', desc: 'Receive updates via email.' },
  { id: 'sw-sms', label: 'SMS notifications', desc: 'Receive updates via text message.' },
  { id: 'sw-push', label: 'Push notifications', desc: 'Receive updates on your device.' },
] as const;

function SwitchDocs() {
  const [invalidEnabled, setInvalidEnabled] = React.useState(false);

  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Switch</h1>
      <p className="mb-4 text-muted-foreground">
        A control that allows the user to toggle between checked and not checked. Use{' '}
        <code className="font-mono text-sm">checked</code> /{' '}
        <code className="font-mono text-sm">defaultChecked</code> and{' '}
        <code className="font-mono text-sm">onCheckedChange</code> to control state.
      </p>
      <p className="mb-8 text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Switch vs Checkbox</strong> — use a switch
        for settings that take effect immediately (e.g. enable notifications, dark mode). Use a{' '}
        <code className="font-mono">DimeCheckbox</code> inside a form when the value is submitted
        with other fields.
      </p>

      {/* Default */}
      <DocsSection title="Default">
        <Preview>
          <DimeField orientation="horizontal" className="items-center gap-3">
            <DimeSwitch id="sw-default" defaultChecked />
            <DimeFieldLabel htmlFor="sw-default">Airplane mode</DimeFieldLabel>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pair with a <code className="font-mono">DimeFieldLabel</code> using matching{' '}
          <code className="font-mono">id</code> / <code className="font-mono">htmlFor</code> so
          clicking the label toggles the switch.
        </p>
      </DocsSection>

      {/* Tone */}
      <DocsSection title="Tone">
        <Preview>
          <DimeField orientation="horizontal" className="items-center gap-3">
            <DimeSwitch id="sw-success" tone="success" defaultChecked />
            <DimeFieldLabel htmlFor="sw-success">Success tone</DimeFieldLabel>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The checked track is the base colour unless <code>tone=&quot;success&quot;</code> is set.
        </p>
      </DocsSection>

      {/* Description */}
      <DocsSection title="Description">
        <Preview>
          <div className="flex flex-col gap-4">
            {NOTIFICATION_OPTIONS.map(({ id, label, desc }) => (
              <DimeField key={id} orientation="horizontal" className="items-start gap-3">
                <DimeSwitch id={id} defaultChecked={id === 'sw-email'} className="mt-0.5" />
                <DimeFieldContent>
                  <DimeFieldLabel htmlFor={id} className="font-medium">
                    {label}
                  </DimeFieldLabel>
                  <DimeFieldDescription>{desc}</DimeFieldDescription>
                </DimeFieldContent>
              </DimeField>
            ))}
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap in a <code className="font-mono">DimeField</code> with{' '}
          <code className="font-mono">DimeFieldLabel</code> and{' '}
          <code className="font-mono">DimeFieldDescription</code> for richer labels.
        </p>
      </DocsSection>

      {/* Choice Card */}
      <DocsSection title="Choice Card">
        <Preview>
          <DimeFieldGroup className="w-full max-w-sm">
            {[
              {
                id: 'card-analytics',
                title: 'Analytics',
                desc: 'Track usage and performance metrics.',
              },
              {
                id: 'card-marketing',
                title: 'Marketing emails',
                desc: 'Receive product updates and offers.',
              },
              {
                id: 'card-security',
                title: 'Security alerts',
                desc: 'Get notified of suspicious activity.',
              },
            ].map(({ id, title, desc }, index) => (
              <DimeFieldLabel key={id} htmlFor={id}>
                <DimeField orientation="horizontal">
                  <DimeFieldContent>
                    <DimeFieldTitle id={`${id}-title`}>{title}</DimeFieldTitle>
                    <DimeFieldDescription>{desc}</DimeFieldDescription>
                  </DimeFieldContent>
                  <DimeSwitch
                    id={id}
                    defaultChecked={index !== 1}
                    aria-labelledby={`${id}-title`}
                  />
                </DimeField>
              </DimeFieldLabel>
            ))}
          </DimeFieldGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap <code className="font-mono">DimeField</code> inside{' '}
          <code className="font-mono">DimeFieldLabel</code> to make the entire card clickable. Group
          cards with <code className="font-mono">DimeFieldGroup</code>. Place{' '}
          <code className="font-mono">DimeSwitch</code> at the trailing edge.
        </p>
      </DocsSection>

      {/* Disabled */}
      <DocsSection title="Disabled">
        <Preview>
          <div className="flex flex-col gap-4">
            <DimeField orientation="horizontal" className="items-center gap-3" data-disabled>
              <DimeSwitch id="sw-dis-off" disabled aria-label="Disabled off switch" />
              <DimeFieldLabel htmlFor="sw-dis-off" className="text-muted-foreground">
                Disabled off
              </DimeFieldLabel>
            </DimeField>
            <DimeField orientation="horizontal" className="items-center gap-3" data-disabled>
              <DimeSwitch id="sw-dis-on" disabled defaultChecked aria-label="Disabled on switch" />
              <DimeFieldLabel htmlFor="sw-dis-on" className="text-muted-foreground">
                Disabled on
              </DimeFieldLabel>
            </DimeField>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> on the{' '}
          <code className="font-mono">DimeSwitch</code> and{' '}
          <code className="font-mono">data-disabled</code> on the wrapping{' '}
          <code className="font-mono">DimeField</code> to grey out the label too.
        </p>
      </DocsSection>

      {/* Invalid */}
      <DocsSection title="Invalid">
        <Preview>
          <DimeField
            orientation="horizontal"
            className="max-w-sm"
            data-invalid={!invalidEnabled || undefined}
          >
            <DimeFieldContent>
              <DimeFieldLabel htmlFor="sw-invalid">Accept terms and conditions</DimeFieldLabel>
              <DimeFieldDescription>
                You must accept the terms and conditions to continue.
              </DimeFieldDescription>
            </DimeFieldContent>
            <DimeSwitch
              id="sw-invalid"
              checked={invalidEnabled}
              onCheckedChange={setInvalidEnabled}
              aria-invalid={!invalidEnabled || undefined}
            />
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">aria-invalid</code> on the{' '}
          <code className="font-mono">DimeSwitch</code> and{' '}
          <code className="font-mono">data-invalid</code> on the wrapping{' '}
          <code className="font-mono">DimeField</code> to show a validation state.
        </p>
      </DocsSection>

      {/* Size */}
      <DocsSection title="Size">
        <Preview>
          <DimeField orientation="horizontal" className="items-center gap-3">
            <DimeSwitch id="sw-sm" size="sm" defaultChecked aria-label="Small switch" />
            <DimeFieldLabel htmlFor="sw-sm" className="text-sm">
              sm
            </DimeFieldLabel>
          </DimeField>
          <DimeField orientation="horizontal" className="items-center gap-3">
            <DimeSwitch id="sw-default-size" defaultChecked aria-label="Default size switch" />
            <DimeFieldLabel htmlFor="sw-default-size" className="text-sm">
              default
            </DimeFieldLabel>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use the <code className="font-mono">size</code> prop to change the switch size. Available
          values: <code className="font-mono">"sm"</code> and{' '}
          <code className="font-mono">"default"</code>.
        </p>
      </DocsSection>

      {/* Props */}
      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { prop: 'checked', type: 'boolean', desc: 'Controlled checked state.' },
              {
                prop: 'defaultChecked',
                type: 'boolean',
                desc: 'Initial checked state (uncontrolled).',
              },
              {
                prop: 'onCheckedChange',
                type: '(checked: boolean) => void',
                desc: 'Fires when state changes.',
              },
              { prop: 'disabled', type: 'boolean', desc: 'Prevents interaction.' },
              { prop: 'size', type: '"default" | "sm"', desc: 'Switch size.' },
              { prop: 'required', type: 'boolean', desc: 'Marks the switch as required.' },
              { prop: 'name', type: 'string', desc: 'Form field name for submission.' },
              { prop: 'value', type: 'string', desc: 'Form field value for submission.' },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
